import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import type React from 'react';
import { useGetTimeToSellQuery } from 'services/api/cars.api';
import { SHADOW, SURFACE, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface SoldCarsAnalysisProps {
  businessUnitId: number;
  onClose: () => void;
}

const NAVY = '#15183A';
const TEAL = '#25616F';
const BLUE = '#2C5889';

const SoldCarsAnalysis: React.FC<SoldCarsAnalysisProps> = ({ businessUnitId, onClose }) => {
  const { data, error, isLoading } = useGetTimeToSellQuery({ businessUnitId });

  const getChartData = () => {
    if (!data) return { labels: [], values: [] };
    return data.buckets;
  };

  const getModelDistributionData = () => {
    if (!data) return [];
    return data.modelStatistics.slice(0, 10).map((model) => ({
      id: model.modelKey,
      label: model.modelName || `Model ${model.modelKey}`,
      value: model.totalCars,
    }));
  };

  const getDailyChartData = () => {
    if (!data?.dailyBuckets) return { labels: [], values: [] };
    const sortedDays = Object.entries(data.dailyBuckets)
      .map(([day, count]) => ({ day: parseInt(day, 10), count }))
      .sort((a, b) => a.day - b.day);
    return {
      labels: sortedDays.map((item) => `${item.day} día${item.day !== 1 ? 's' : ''}`),
      values: sortedDays.map((item) => item.count),
    };
  };

  const getSpeedCategories = () => {
    if (!data) return { fast: [], slow: [] };
    const sortedBySpeed = [...data.modelStatistics].sort((a, b) => a.percentile10 - b.percentile10);
    return {
      fast: sortedBySpeed.slice(0, 3),
      slow: sortedBySpeed.slice(-3),
    };
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error: {JSON.stringify(error)}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No hay datos disponibles
      </Alert>
    );
  }

  const chartData = getChartData();
  const modelDistribution = getModelDistributionData();
  const speedCategories = getSpeedCategories();
  const dailyChartData = getDailyChartData();

  return (
    <Box sx={{ backgroundColor: SURFACE, minHeight: '100vh', py: 4, px: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
          <Tooltip title="Volver">
            <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h4">Análisis de Ventas</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Estadísticas de tiempo de venta y distribución por modelo
            </Typography>
          </Box>
        </Box>

        {/* Summary Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
          <Box
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              p: 3,
              boxShadow: SHADOW,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
              Total Autos Vendidos
            </Typography>
            <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: NAVY, lineHeight: 1.1 }}>
              {data.totalSoldCars}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              p: 3,
              boxShadow: SHADOW,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
              Tiempo Promedio de Venta
            </Typography>
            <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: NAVY, lineHeight: 1.1 }}>
              {data.averageTimeToSell}
              <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 400, color: 'text.secondary', ml: 0.5 }}>
                días
              </Typography>
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              p: 3,
              boxShadow: SHADOW,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
              Modelos Analizados
            </Typography>
            <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: NAVY, lineHeight: 1.1 }}>
              {data.modelStatistics.length}
            </Typography>
          </Box>
        </Box>

        {/* Time to Sell Distribution Chart */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            p: 3,
            mb: 3,
            boxShadow: SHADOW,
          }}
        >
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Tiempo en vender los autos
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Agrupado por intervalo de días
          </Typography>
          <Box sx={{ height: 400 }}>
            <BarChart
              xAxis={[{ scaleType: 'band', data: chartData.labels }]}
              series={[{ data: chartData.values, color: NAVY }]}
              height={350}
            />
          </Box>
        </Box>

        {/* Daily Buckets Chart */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            p: 3,
            mb: 3,
            boxShadow: SHADOW,
          }}
        >
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Distribución de ventas por día
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Cantidad de autos vendidos por día específico
          </Typography>
          <Box sx={{ height: 400 }}>
            <BarChart
              xAxis={[{ scaleType: 'band', data: dailyChartData.labels }]}
              series={[{ data: dailyChartData.values, color: TEAL }]}
              height={350}
            />
          </Box>
        </Box>

        {/* Top 10 Models Chart */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            p: 3,
            mb: 3,
            boxShadow: SHADOW,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Top 10 modelos más vendidos
          </Typography>
          <Box sx={{ height: 300 }}>
            <BarChart
              xAxis={[{ scaleType: 'band', data: modelDistribution.map((item) => item.label) }]}
              series={[{ data: modelDistribution.map((item) => item.value), color: BLUE }]}
              height={280}
            />
          </Box>
        </Box>

        {/* Speed Categories */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            p: 3,
            mb: 3,
            boxShadow: SHADOW,
          }}
        >
          <Typography variant="h6" sx={{ mb: 3 }}>
            Modelos por velocidad de venta
          </Typography>

          {/* Fast Selling */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: TEAL, mb: 1.5 }}>
              Venta Rápida
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              {speedCategories.fast.map((model) => (
                <Box
                  key={model.modelKey}
                  sx={{
                    backgroundColor: SURFACE_CONTAINER_LOW,
                    borderRadius: 2,
                    p: 2.5,
                    borderLeft: `3px solid ${TEAL}`,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    {model.modelName || `Model ${model.modelKey}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {model.totalCars} autos — Promedio: {model.averageTimeToSell} días
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    P10: {model.percentile10}d — P90: {model.percentile90}d
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Slow Selling */}
          <Box>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5 }}>
              Venta Lenta — Requieren Atención
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              {speedCategories.slow.map((model) => (
                <Box
                  key={model.modelKey}
                  sx={{
                    backgroundColor: SURFACE_CONTAINER_LOW,
                    borderRadius: 2,
                    p: 2.5,
                    borderLeft: `3px solid ${'rgba(21, 24, 58, 0.12)'}`,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    {model.modelName || `Model ${model.modelKey}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {model.totalCars} autos — Promedio: {model.averageTimeToSell} días
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    P10: {model.percentile10}d — P90: {model.percentile90}d
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Detailed Model Statistics Table */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            p: 3,
            boxShadow: SHADOW,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Estadísticas Detalladas por Modelo
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
              <Box component="thead">
                <Box component="tr">
                  {['Modelo', 'Total Autos', 'Promedio', 'P10', 'P90', 'Rango'].map((header, i) => (
                    <Box
                      component="th"
                      key={header}
                      sx={{
                        p: 1.5,
                        textAlign: i === 0 ? 'left' : 'center',
                        backgroundColor: SURFACE_CONTAINER_LOW,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'text.secondary',
                        '&:first-of-type': { borderRadius: '8px 0 0 8px' },
                        '&:last-of-type': { borderRadius: '0 8px 8px 0' },
                      }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {data.modelStatistics.map((model, index) => (
                  <Box
                    component="tr"
                    key={model.modelKey}
                    sx={{
                      backgroundColor: index % 2 === 0 ? SURFACE_CONTAINER_LOWEST : SURFACE_CONTAINER_LOW,
                      transition: 'background-color 0.15s ease',
                      '&:hover': { backgroundColor: SURFACE_CONTAINER_LOW },
                    }}
                  >
                    <Box component="td" sx={{ p: 1.5, fontWeight: 600, borderRadius: '8px 0 0 8px' }}>
                      {model.modelName || `Model ${model.modelKey}`}
                    </Box>
                    <Box component="td" sx={{ p: 1.5, textAlign: 'center' }}>
                      {model.totalCars}
                    </Box>
                    <Box component="td" sx={{ p: 1.5, textAlign: 'center' }}>
                      {model.averageTimeToSell} días
                    </Box>
                    <Box component="td" sx={{ p: 1.5, textAlign: 'center' }}>
                      {model.percentile10} días
                    </Box>
                    <Box component="td" sx={{ p: 1.5, textAlign: 'center' }}>
                      {model.percentile90} días
                    </Box>
                    <Box component="td" sx={{ p: 1.5, textAlign: 'center', borderRadius: '0 8px 8px 0' }}>
                      {model.minTimeToSell} - {model.maxTimeToSell} días
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SoldCarsAnalysis;
